<template>
  <div class="relative space-y-6 lg:space-y-8 px-3 sm:px-4 lg:px-8 min-h-[calc(100vh-8rem)] pt-6">
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
      <div>
        <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary dark:text-secondary">
          My Documents
        </h1>
        <p class="text-sm text-slate-500 dark:text-slate-200 mt-2">
          Upload and manage your important documents.
        </p>
      </div>
      <button @click="showUploadModal = true"
        class="px-6 py-2 rounded-lg font-semibold text-white transition bg-primary hover:bg-secondary
               shadow-md hover:opacity-90 flex items-center justify-center w-full sm:w-auto">
        <FilePlusCorner class="h-5 w-5 mr-2" />
        Upload Document
      </button>
    </div>

    <TableControls
      v-model:search="searchQuery"
      v-model:status="statusFilter"
      v-model:perPage="perPage"
      placeholder="Search by title..."
      :options="[{ value: '', label: 'All' }]"
      filterLabel="Filter"
      class="md:sticky md:top-[80px]"
    />

    <div v-if="error" class="bg-red-50 text-red-600 p-4 rounded-xl border border-red-200">
      {{ error }}
    </div>

    <div v-else class="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200/50 dark:border-slate-700/50 overflow-hidden">
      <!-- Desktop Table View -->
      <div class="hidden lg:block overflow-x-auto">
        <table class="min-w-full divide-y divide-slate-200/50 dark:divide-slate-700/50">
          <thead class="bg-slate-50 dark:bg-slate-800/50">
            <tr>
              <th class="px-6 py-4 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">Title</th>
              <th class="px-6 py-4 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">Uploaded At</th>
              <th class="px-6 py-4 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">Status</th>
              <th class="px-6 py-4 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200/50 dark:divide-slate-700/50">
            <TableLoader v-if="loading" :colspan="4" title="Fetching Documents" />
            <tr v-else-if="paginatedDocuments.length === 0">
              <td colspan="4" class="px-6 py-12 text-center text-slate-500">
                You haven't uploaded any documents yet.
              </td>
            </tr>
            <tr v-for="doc in paginatedDocuments" :key="doc.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
              <td class="px-6 py-4 text-center text-sm font-medium text-slate-700 dark:text-slate-300">{{ doc.title }}</td>
              <td class="px-6 py-4 text-center text-sm text-slate-500 dark:text-slate-400">{{ new Date(doc.created_at).toLocaleString() }}</td>
              <td class="px-6 py-4 text-center">
                <span class="px-3 py-1 text-xs font-semibold rounded-full"
                  :class="doc.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'">
                  {{ doc.status }}
                </span>
              </td>
              <td class="px-6 py-4">
                <div class="flex gap-2 justify-center">
                  <button @click="viewDocument(doc)" class="p-2 text-blue-600 hover:bg-primary/10 rounded-lg transition-colors" title="View">
                    <Eye class="w-5 h-5" />
                  </button>
                  <button @click="downloadDocument(doc)" class="p-2 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors" title="Download">
                    <DownloadIcon class="w-5 h-5" />
                  </button>
                  <button @click="printDocument(doc)" class="p-2 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors" title="Print">
                    <Printer class="w-5 h-5" />
                  </button>
                  <button @click="openEditModal(doc)" class="p-2 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Edit">
                    <Pencil class="w-5 h-5" />
                  </button>
                  <button @click="handleDelete(doc)" class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Delete">
                    <Trash class="w-5 h-5" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile Card View -->
      <div class="lg:hidden divide-y divide-slate-200/50 dark:divide-slate-700/50">
        <div v-if="loading" class="p-8 flex justify-center">
          <Loader2 class="w-8 h-8 animate-spin text-primary" />
        </div>
        <div v-else-if="paginatedDocuments.length === 0" class="px-6 py-12 text-center text-slate-500">You haven't uploaded any documents yet.</div>
        <div v-else v-for="doc in paginatedDocuments" :key="doc.id" class="p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50">
          <div class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{{ doc.title }}</div>
          <div class="flex items-center gap-2 mb-2">
            <span class="px-2 py-1 text-xs font-semibold rounded-full" :class="doc.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'">{{ doc.status }}</span>
            <span class="text-xs text-slate-500 dark:text-slate-400">{{ new Date(doc.created_at).toLocaleString() }}</span>
          </div>
          <div class="flex gap-2">
            <button @click="viewDocument(doc)" class="p-2 text-blue-600 hover:bg-primary/10 rounded-lg transition-colors" title="View">
              <Eye class="w-5 h-5" />
            </button>
            <button @click="downloadDocument(doc)" class="p-2 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors" title="Download">
              <DownloadIcon class="w-5 h-5" />
            </button>
            <button @click="printDocument(doc)" class="p-2 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors" title="Print">
              <Printer class="w-5 h-5" />
            </button>
            <button @click="openEditModal(doc)" class="p-2 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Edit">
              <Pencil class="w-5 h-5" />
            </button>
            <button @click="handleDelete(doc)" class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Delete">
              <Trash class="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="!loading && !error && filteredTotal > 0"
      class="card border border-gray-200/50 dark:border-gray-700/50 shadow-sm sticky bottom-0 z-30"
    >
      <div class="p-4">
        <PaginationBar
          :current-page="currentPage"
          :total-pages="filteredTotalPages"
          :total="filteredTotal"
          :per-page="perPage"
          @update:page="goToPage"
        />
      </div>
    </div>

    <!-- Upload Document Modal -->
    <transition name="fade">
      <div v-if="showUploadModal" class="fixed inset-0 z-50 overflow-y-auto" @click.self="showUploadModal = false">
        <div class="flex sm:items-center sm:justify-center min-h-screen sm:px-4 sm:pt-4 sm:pb-20 text-center">
          <div class="fixed inset-0 transition-opacity bg-black bg-opacity-50 dark:bg-opacity-50"></div>
          <div class="inline-block align-bottom sm:align-middle bg-white dark:bg-gray-800 sm:rounded-lg text-left overflow-hidden shadow-xl transform transition-all min-h-screen w-full fixed inset-x-0 top-0 sm:static sm:min-h-0 sm:max-w-lg sm:w-full flex flex-col">
            <div class="bg-gradient-to-tr from-primary to-secondary dark:from-primary-light/10 dark:to-secondary-light/10 px-4 py-4 sm:px-6 flex items-center justify-between flex-shrink-0">
              <h3 class="text-lg font-bold text-white truncate">Upload Document</h3>
              <button @click="showUploadModal = false" class="shrink-0 p-1.5 rounded-lg hover:bg-white/10 text-white/80 hover:text-white transition-colors">
                <X class="h-5 w-5" />
              </button>
            </div>
            <div class="bg-white dark:bg-gray-800 px-3 pt-4 pb-3 sm:p-6 sm:pb-4 overflow-y-auto flex-1 max-h-[calc(100vh-12rem)]">
              <div class="space-y-4">
                <div>
                  <label class="text-sm font-medium text-slate-700 dark:text-slate-200">Document Title <span class="text-red-500">*</span></label>
                  <input v-model="uploadTitle" type="text" class="mt-1 w-full rounded-xl border px-3 py-2 bg-white dark:bg-slate-900" placeholder="e.g. CPR Certification" />
                </div>
                <div>
                  <label class="text-sm font-medium text-slate-700 dark:text-slate-200">File <span class="text-red-500">*</span></label>
                  <div class="mt-1 rounded-2xl border-2 border-dashed p-6 cursor-pointer select-none bg-white/60 dark:bg-gray-800/50 border-gray-300/70 dark:border-gray-600/60 hover:bg-white/80 dark:hover:bg-gray-800/70" :class="uploadDragOver ? 'ring-2 ring-primary/50 border-primary/60' : ''" @click="openUploadFilePicker" @dragenter.prevent="uploadDragOver = true" @dragover.prevent="uploadDragOver = true" @dragleave.prevent="uploadDragOver = false" @drop.prevent="onUploadDrop">
                    <div class="flex items-center gap-4 w-full">
                      <div class="h-11 w-11 rounded-xl flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200">
                        <UploadIcon />
                      </div>
                      <div class="min-w-0 flex-1 overflow-hidden">
                        <template v-if="uploadFile">
                          <p class="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate" :title="uploadFile.name">{{ uploadFile.name }}</p>
                          <p class="text-xs text-gray-500 dark:text-gray-400">Click or drop to change file</p>
                        </template>
                        <template v-else>
                          <p class="text-sm font-semibold text-gray-800 dark:text-gray-100">Drag &amp; drop your file here</p>
                          <p class="text-xs text-gray-500 dark:text-gray-400">or click to browse</p>
                        </template>
                      </div>
                    </div>
                  </div>
                  <input ref="uploadFileInput" type="file" class="hidden" accept="*/*" @change="onUploadFileChange" />
                </div>
                <p v-if="uploadError" class="text-sm text-red-500">{{ uploadError }}</p>
              </div>
            </div>
            <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 flex flex-col gap-2 sm:px-6 sm:flex-row-reverse sm:gap-0 shrink-0">
              <button type="button" class="w-full sm:w-auto sm:ml-3 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-primary hover:bg-secondary shadow-sm transition-all disabled:opacity-60" :disabled="uploading" @click="uploadDocument">{{ uploading ? 'Uploading...' : 'Upload' }}</button>
              <button type="button" class="w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" @click="showUploadModal = false">Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Delete Confirmation Modal -->
    <Modal v-model="showDeleteModal" title="Delete Document">
      <p class="text-sm text-secondary-text/80 dark:text-secondary-dark-text/80">
        Are you sure you want to permanently delete
        <span class="font-semibold text-slate-700 dark:text-slate-300">"{{ pendingDeleteTitle }}"</span>?
        This action cannot be undone.
      </p>
      <template #footer>
        <button type="button"
          class="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-secondary-text dark:text-secondary-dark-text hover:bg-gray-100/80 dark:hover:bg-gray-700/60 transition-colors duration-200"
          @click="showDeleteModal = false">Cancel</button>
        <button type="button"
          class="ml-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-danger hover:bg-danger/90 shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
          :disabled="deleteLoading" @click="confirmDelete">
          {{ deleteLoading ? 'Deleting...' : 'Yes, delete permanently' }}
        </button>
      </template>
    </Modal>

    <!-- Edit Modal -->
    <transition name="fade">
      <div v-if="showEditModal" class="fixed inset-0 z-50 overflow-y-auto" @click.self="showEditModal = false">
        <div class="flex sm:items-center sm:justify-center min-h-screen sm:px-4 sm:pt-4 sm:pb-20 text-center">
          <div class="fixed inset-0 transition-opacity bg-black bg-opacity-50 dark:bg-opacity-50"></div>
          <div class="inline-block align-bottom sm:align-middle bg-white dark:bg-gray-800 sm:rounded-lg text-left overflow-hidden shadow-xl transform transition-all min-h-screen w-full fixed inset-x-0 top-0 sm:static sm:min-h-0 sm:max-w-lg sm:w-full flex flex-col">
            <div class="bg-gradient-to-tr from-primary to-secondary dark:from-primary-light/10 dark:to-secondary-light/10 px-4 py-4 sm:px-6 flex items-center justify-between flex-shrink-0">
              <h3 class="text-lg font-bold text-white truncate">Edit Document</h3>
              <button @click="showEditModal = false" class="shrink-0 p-1.5 rounded-lg hover:bg-white/10 text-white/80 hover:text-white transition-colors">
                <X class="h-5 w-5" />
              </button>
            </div>
            <div class="bg-white dark:bg-gray-800 px-3 pt-4 pb-3 sm:p-6 sm:pb-4 overflow-y-auto flex-1 max-h-[calc(100vh-12rem)]">
              <div class="space-y-4">
                <div>
                  <label class="text-sm font-medium text-slate-700 dark:text-slate-200">Document Title <span class="text-red-500">*</span></label>
                  <input v-model="editTitle" type="text" class="mt-1 w-full rounded-xl border px-3 py-2 bg-white dark:bg-slate-900" placeholder="e.g. CPR Certification" />
                </div>
                <div>
                  <label class="text-sm font-medium text-slate-700 dark:text-slate-200">File (leave empty to keep current)</label>
                  <div class="mt-1 rounded-2xl border-2 border-dashed p-6 cursor-pointer select-none bg-white/60 dark:bg-gray-800/50 border-gray-300/70 dark:border-gray-600/60 hover:bg-white/80 dark:hover:bg-gray-800/70" :class="editDragOver ? 'ring-2 ring-primary/50 border-primary/60' : ''" @click="openEditFilePicker" @dragenter.prevent="editDragOver = true" @dragover.prevent="editDragOver = true" @dragleave.prevent="editDragOver = false" @drop.prevent="onEditDrop">
                    <div class="flex items-center gap-4 w-full">
                      <div class="h-11 w-11 rounded-xl flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200">
                        <UploadIcon />
                      </div>
                      <div class="min-w-0 flex-1 overflow-hidden">
                        <template v-if="editFile">
                          <p class="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate" :title="editFile.name">{{ editFile.name }}</p>
                          <p class="text-xs text-gray-500 dark:text-gray-400">Click or drop to change file</p>
                        </template>
                        <template v-else>
                          <p class="text-sm font-semibold text-gray-800 dark:text-gray-100">Drag &amp; drop your file here</p>
                          <p class="text-xs text-gray-500 dark:text-gray-400">or click to browse</p>
                        </template>
                      </div>
                    </div>
                  </div>
                  <input ref="editFileInput" type="file" class="hidden" accept="*/*" @change="onEditFileChange" />
                </div>
                <p v-if="editError" class="text-sm text-red-500">{{ editError }}</p>
              </div>
            </div>
            <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 flex flex-col gap-2 sm:px-6 sm:flex-row-reverse sm:gap-0 shrink-0">
              <button type="button" class="w-full sm:w-auto sm:ml-3 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-primary hover:bg-secondary shadow-sm transition-all disabled:opacity-50" :disabled="editLoading" @click="confirmEdit">{{ editLoading ? 'Saving...' : 'Save' }}</button>
              <button type="button" class="w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" @click="showEditModal = false">Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- View Document Modal -->
    <DocumentDetailsModal
      :is-open="showViewModal"
      :document="selectedDocument"
      @close="showViewModal = false; selectedDocument = null"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import UploadIcon from '@/components/icons/UploadIcon.vue'
import { FilePlusCorner, Eye, Trash, Download as DownloadIcon, Printer, Pencil, Loader2, X } from 'lucide-vue-next'
import { employeeDocumentService, type EmployeeDocument } from '@/services/employeeDocumentService'
import Modal from '@/components/layout/Modal.vue'
import DocumentDetailsModal from '@/components/documents/DocumentDetailsModal.vue'
import TableControls from '@/components/layout/TableControls.vue'
import PaginationBar from '@/components/layout/PaginationBar.vue'
import TableLoader from '@/components/ui/TableLoader.vue'
import { useToast } from 'vue-toastification'

const toast = useToast()
const toastTimeout = 5000

const documents = ref<EmployeeDocument[]>([])
const loading = ref(false)
const error = ref('')

const searchQuery = ref('')
const statusFilter = ref('')
const perPage = ref(10)
const currentPage = ref(1)

const showUploadModal = ref(false)
const uploadTitle = ref('')
const uploadFile = ref<File | null>(null)
const uploadError = ref('')
const uploading = ref(false)
const uploadFileInput = ref<HTMLInputElement | null>(null)
const uploadDragOver = ref(false)

const searchFiltered = computed(() => {
  if (!searchQuery.value.trim()) return documents.value
  const q = searchQuery.value.toLowerCase()
  return documents.value.filter(d => (d.title || '').toLowerCase().includes(q))
})

const filteredTotal = computed(() => searchFiltered.value.length)
const filteredTotalPages = computed(() => Math.max(1, Math.ceil(searchFiltered.value.length / perPage.value)))

const paginatedDocuments = computed(() => {
  const start = (currentPage.value - 1) * perPage.value
  return searchFiltered.value.slice(start, start + perPage.value)
})

watch([searchQuery, statusFilter, perPage], () => {
  currentPage.value = 1
})

const goToPage = (page: number) => {
  currentPage.value = page
}

const fetchDocuments = async () => {
  loading.value = true
  error.value = ''
  try {
    documents.value = await employeeDocumentService.fetchAll()
  } catch (err: any) {
    error.value = 'Failed to load your documents.'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchDocuments()
})

const openUploadFilePicker = () => uploadFileInput.value?.click()

const onUploadFileChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  const file = target.files?.[0] ?? null
  if (file) uploadFile.value = file
}

const onUploadDrop = (e: DragEvent) => {
  uploadDragOver.value = false
  const file = e.dataTransfer?.files?.[0] ?? null
  if (file) uploadFile.value = file
}

const uploadDocument = async () => {
  uploadError.value = ''
  if (!uploadTitle.value.trim()) {
    uploadError.value = 'Title is required.'
    return
  }
  if (!uploadFile.value) {
    uploadError.value = 'Please select a file.'
    return
  }

  const formData = new FormData()
  formData.append('title', uploadTitle.value.trim())
  formData.append('file', uploadFile.value)

  uploading.value = true
  try {
    await employeeDocumentService.create(formData)
    toast.success('Document uploaded successfully', { timeout: toastTimeout })
    showUploadModal.value = false
    uploadTitle.value = ''
    uploadFile.value = null
    fetchDocuments()
  } catch (err: any) {
    uploadError.value = err.response?.data?.message || 'Failed to upload document.'
  } finally {
    uploading.value = false
  }
}

const showDeleteModal = ref(false)
const deleteLoading = ref(false)
const pendingDeleteId = ref<number | null>(null)
const pendingDeleteTitle = ref('')

const showViewModal = ref(false)
const selectedDocument = ref<EmployeeDocument | null>(null)
const showEditModal = ref(false)
const editTitle = ref('')
const editFile = ref<File | null>(null)
const editError = ref('')
const editLoading = ref(false)
const editingDocId = ref<number | null>(null)
const editFileInput = ref<HTMLInputElement | null>(null)
const editDragOver = ref(false)

const viewDocument = (doc: EmployeeDocument) => {
  selectedDocument.value = doc
  showViewModal.value = true
}

const downloadDocument = async (doc: EmployeeDocument) => {
  const loadingToast = toast.info('Preparing document for download...', { timeout: false })
  try {
    const token = localStorage.getItem('token')
    const url = employeeDocumentService.getDownloadUrl(doc.id)
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) throw new Error('Invalid File')
    const blob = await response.blob()
    const blobUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = doc.title || 'download'
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(blobUrl)
    document.body.removeChild(a)
    toast.dismiss(loadingToast)
    toast.success('Document downloaded successfully', { timeout: toastTimeout })
  } catch {
    toast.dismiss(loadingToast)
    toast.error('Failed to download document', { timeout: toastTimeout })
  }
}

const printDocument = async (doc: EmployeeDocument) => {
  const loadingToast = toast.info('Preparing document for printing...', { timeout: false })
  try {
    await employeeDocumentService.printDocument(doc.id)
    toast.dismiss(loadingToast)
  } catch (err: any) {
    toast.dismiss(loadingToast)
    if (!err.message?.includes('opened in a new tab')) {
      toast.error(err.message || 'Failed to print document', { timeout: toastTimeout })
    }
  }
}

const handleDelete = (doc: EmployeeDocument) => {
  pendingDeleteId.value = doc.id
  pendingDeleteTitle.value = doc.title || 'this document'
  showDeleteModal.value = true
}

const confirmDelete = async () => {
  if (pendingDeleteId.value === null) return
  deleteLoading.value = true
  try {
    await employeeDocumentService.delete(pendingDeleteId.value)
    toast.success('Document deleted', { timeout: toastTimeout })
    showDeleteModal.value = false
    pendingDeleteId.value = null
    fetchDocuments()
  } catch {
    toast.error('Failed to delete document', { timeout: toastTimeout })
  } finally {
    deleteLoading.value = false
  }
}

const openEditFilePicker = () => editFileInput.value?.click()

const onEditDrop = (e: DragEvent) => {
  editDragOver.value = false
  const file = e.dataTransfer?.files?.[0] ?? null
  if (file) editFile.value = file
}

const openEditModal = (doc: EmployeeDocument) => {
  editingDocId.value = doc.id
  editTitle.value = doc.title
  editFile.value = null
  editError.value = ''
  showEditModal.value = true
}

const onEditFileChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  editFile.value = target.files?.[0] || null
}

const confirmEdit = async () => {
  if (!editingDocId.value) return
  if (!editTitle.value.trim()) {
    editError.value = 'Title is required.'
    return
  }
  const formData = new FormData()
  formData.append('title', editTitle.value.trim())
  if (editFile.value) {
    formData.append('file', editFile.value)
  }
  editLoading.value = true
  editError.value = ''
  try {
    await employeeDocumentService.update(editingDocId.value, formData)
    toast.success('Document updated successfully', { timeout: toastTimeout })
    showEditModal.value = false
    fetchDocuments()
  } catch (err: unknown) {
    const axiosErr = err as { response?: { data?: { message?: string } } }
    editError.value = axiosErr.response?.data?.message || 'Failed to update document.'
  } finally {
    editLoading.value = false
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
